import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, PageHeader, PageState, SearchField, SegmentedControl } from "@fox1ck-org/ui";
import { DataTable } from "@fox1ck-org/ui/data-table";
import { TablePagination } from "@fox1ck-org/ui/table";
import { LayoutGrid, Plus, Rows3, ShieldHalf } from "lucide-react";
import type { SortingState } from "@tanstack/react-table";

import { WriteOnly } from "@/components/auth/write-only";
import { LanderCreate, type CreateSource } from "@/components/landers/create";
import { LanderEmpty } from "@/components/landers/empty";
import { FACET_IDS, LanderFacets, readFilter } from "@/components/landers/facets";
import { LibraryKpis } from "@/components/landers/kpis";
import { LanderTiles, useLanderColumns, type LanderLine } from "@/components/landers/library";
import { LANDER_METRICS, bagOf } from "@/components/landers/numbers";
import { formatInt } from "@/lib/format";
import { useTrackerRoles } from "@/lib/permissions";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { plural, useT } from "@/lib/i18n";
import { listSort } from "@/lib/services/catalog";
import { useLanderLibrary, useListLanderNumbers, useListLanders } from "@/lib/services/lander-service";
import { useReportingSchema } from "@/lib/services/reporting-schema-service";

type Layout = "table" | "tiles";

/** The columns the server orders landers by. */
const SORT_FIELDS: Record<string, string> = { name: "name", risk_status: "risk_status", updated_at: "updated_at" };
const DEFAULT_SORT: SortingState = [{ id: "updated_at", desc: true }];
const PAGE_SIZES = [50, 100, 250] as const;
const LAYOUT_KEY = "vibe-tracker.landers.layout";

function readLayout(): Layout {
  try {
    return window.localStorage.getItem(LAYOUT_KEY) === "tiles" ? "tiles" : "table";
  } catch {
    return "table";
  }
}

/**
 * Ленды (boards LanderList, LanderTiles, LanderEmpty): the library on the
 * server — searched, filtered, ordered and paged there — as a table or as
 * tiles with the page itself, every number the report engine's for the app's
 * period (ListLanderNumbers). A lander opens as its own page.
 */
export default function LandersListPage() {
  const t = useT();
  const roles = useTrackerRoles();
  const navigate = useNavigate();
  const params = useTableParams({ defaultPageSize: 50, pageSizeOptions: PAGE_SIZES, defaultSort: DEFAULT_SORT });
  // Which layout to draw is a per-viewer habit, not list state: it stays in
  // this browser rather than in a link someone else opens.
  const [layout, setLayoutState] = useState<Layout>(readLayout);
  const setLayout = (next: Layout) => {
    setLayoutState(next);
    try {
      window.localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      /* a private window keeps it for the session */
    }
  };
  const [creating, setCreating] = useState<CreateSource | null>(null);

  const filter = readFilter(params.getFilter);
  const sorting = params.sorting.length > 0 ? params.sorting : DEFAULT_SORT;
  const list = useListLanders({
    search: params.search.trim(),
    types: filter.types,
    languages: filter.languages,
    countries: filter.countries,
    risks: filter.risks,
    usage: filter.usage,
    sort: listSort(sorting, SORT_FIELDS),
    limit: params.pageSize,
    offset: params.pageIndex * params.pageSize,
  });
  const library = useLanderLibrary();
  const schema = useReportingSchema();
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const ids = useMemo(() => items.map((l) => l.id), [items]);
  const numbers = useListLanderNumbers({
    ids,
    from: params.from,
    to: params.to,
    timezone: params.tz,
    basis: params.basis,
    metrics: LANDER_METRICS,
  });
  const lines = useMemo<LanderLine[]>(
    () =>
      items.map((l) => {
        const row = numbers.data?.rows.get(l.id);
        return { id: l.id, lander: l, v: bagOf(row?.stats, schema.data), campaigns: row?.campaigns };
      }),
    [items, numbers.data, schema.data],
  );
  const columns = useLanderColumns();

  const total = list.data?.total ?? 0;
  const filtered = !!params.search || FACET_IDS.some((id) => params.getFilter(id));
  const sortName = t(sorting[0]?.id === "name" ? "landers.sort.name" : sorting[0]?.id === "risk_status" ? "landers.sort.risk" : "landers.sort.updated");
  const arrow = sorting[0]?.desc ? "↓" : "↑";
  const open = (id: string) => navigate(`/library/landers/${id}`);

  const header = (
    <PageHeader
      title={t("landers.title")}
      subtitle={t("landers.subtitle")}
      actions={
        <>
          <SegmentedControl<Layout>
            size="sm"
            aria-label={t("landers.layout")}
            value={layout}
            onValueChange={setLayout}
            options={[
              { value: "table", label: t("landers.layout.table"), icon: <Rows3 className="size-4" strokeWidth={1.5} /> },
              { value: "tiles", label: t("landers.layout.tiles"), icon: <LayoutGrid className="size-4" strokeWidth={1.5} /> },
            ]}
          />
          {roles.isTrackerAdmin || roles.isPlatformAdmin ? (
            <Button asChild variant="secondary">
              <Link to="/white-pool">
                <ShieldHalf className="size-4" strokeWidth={1.5} aria-hidden />
                {t("white.nav")}
              </Link>
            </Button>
          ) : null}
          <WriteOnly>
            <Button onClick={() => setCreating("zip")}>
              <Plus className="size-4" strokeWidth={2} />
              {t("landers.new")}
            </Button>
          </WriteOnly>
        </>
      }
    />
  );

  const create = <LanderCreate source={creating} onSourceChange={setCreating} />;

  if (list.error) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
        <PageHeader title={t("landers.title")} />
        <PageState mode="error" title={t("landers.loadFailed")} error={list.error} retry={() => void list.refetch()} retryLabel={t("lib.common.retry")} />
      </div>
    );
  }
  if (library.data && Number(library.data.landers) === 0 && !filtered && !list.isLoading && total === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
        <PageHeader title={t("landers.title")} />
        <LanderEmpty onCreate={setCreating} />
        {create}
      </div>
    );
  }

  const toolbar = (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <SearchField
        value={params.search}
        onChange={params.setSearch}
        label={t("landers.search.label")}
        placeholder={t("landers.search")}
        clearLabel={t("landers.search.clear")}
        className="w-65"
      />
      <LanderFacets params={params} />
      <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
        {t("landers.count", { n: formatInt(total), landers: plural(t("landers.word.landers"), total) })} · {t("landers.sortedBy", { by: sortName })} {arrow}
      </span>
    </div>
  );
  const pagination = {
    page: params.pageIndex + 1,
    pageSize: params.pageSize,
    totalCount: total,
    onPageChange: (page: number) => params.setPageIndex(page - 1),
    onPageSizeChange: params.setPageSize,
    pageSizeOptions: PAGE_SIZES,
    labels: {
      previous: t("landers.page.prev"),
      next: t("landers.page.next"),
      summary: (from: number, to: number, all: number) => t("landers.page.summary", { from: formatInt(from), to: formatInt(to), total: formatInt(all) }),
      page: (page: number, pages: number) => t("landers.page.of", { page: formatInt(page), pages: formatInt(pages) }),
      perPage: t("landers.page.perPage"),
    },
  };
  const noneMatch = (
    <PageState
      mode="empty"
      title={t("landers.filtered.title")}
      description={t("landers.filtered.body")}
      action={
        <Button variant="secondary" size="sm" onClick={params.resetFilters}>
          {t("landers.filtered.reset")}
        </Button>
      }
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      {header}
      {layout === "table" ? (
        <>
          <LibraryKpis />
          {toolbar}
          <DataTable
            className="min-h-0 flex-1"
            fill
            density="compact"
            columns={columns}
            data={lines}
            getRowId={(r) => r.id}
            loading={list.isLoading}
            sorting={sorting}
            manualSorting
            onSortingChange={(updater) => params.setSorting(typeof updater === "function" ? updater(sorting) : updater)}
            onRowClick={(r) => open(r.id)}
            pagination={pagination}
            caption={t("landers.title")}
            emptyState={noneMatch}
            labels={{ empty: t("landers.filtered.title"), stackedRow: (r) => (r as LanderLine).lander.name }}
          />
        </>
      ) : (
        <>
          {toolbar}
          <div className="min-h-0 flex-1 overflow-y-auto">{lines.length === 0 && !list.isLoading ? noneMatch : <LanderTiles lines={lines} onOpen={open} />}</div>
          <TablePagination {...pagination} className="shrink-0 rounded-lg border border-border bg-card px-3" />
        </>
      )}
      {create}
    </div>
  );
}
